/* InstallAppButton — renders a friendly "Install app" button that:
   - On browsers that support the W3C beforeinstallprompt event (Chrome, Edge,
     Samsung Internet on Android / desktop), directly triggers the native
     install prompt.
   - On iOS Safari / Chrome, which do not fire beforeinstallprompt, opens a
     small modal with "Share → Add to Home Screen" instructions.
   - Self-hides when already running standalone (display-mode: standalone). */
import React, { useEffect, useState, useCallback } from 'react';
import { Smartphone, X, Share } from 'lucide-react';

function isStandalone() {
    if (typeof window === 'undefined') return false;
    return (
        window.matchMedia && window.matchMedia('(display-mode: standalone)').matches
    ) || window.navigator.standalone === true;
}

function isIOS() {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
}

export default function InstallAppButton({ className = '' }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showIOS, setShowIOS] = useState(false);
    const [installed, setInstalled] = useState(isStandalone());

    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    const onClick = useCallback(async () => {
        if (deferredPrompt) {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') setInstalled(true);
            setDeferredPrompt(null);
            return;
        }
        // No native prompt → show iOS instructions (and Android hint if PWA
        // criteria are not yet met because the user hasn't visited twice).
        setShowIOS(true);
    }, [deferredPrompt]);

    if (installed) return null;

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                data-testid="install-app-btn"
                className={`inline-flex items-center gap-2 bg-lq-ink-900 hover:bg-lq-ink-black text-lq-paper px-4 py-2 rounded-full text-sm font-semibold transition-colors ${className}`}
            >
                <Smartphone size={16} /> Install app
            </button>

            {showIOS && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="install-app-modal">
                    <div className="relative bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
                        <button onClick={() => setShowIOS(false)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-lq-ink-100 inline-flex items-center justify-center" aria-label="Close"><X size={14}/></button>
                        <div className="px-6 pt-6 pb-4">
                            <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500">Install Linguistic Quest</div>
                            <h3 className="mt-1 font-display text-2xl text-lq-ink-900">Add to Home Screen</h3>
                            {isIOS() ? (
                                <ol className="mt-4 space-y-3 text-sm text-lq-ink-700 list-decimal list-inside">
                                    <li>Tap the <span className="inline-flex items-center gap-1 font-semibold"><Share size={14}/> Share</span> icon at the bottom of Safari.</li>
                                    <li>Scroll and tap <strong>Add to Home Screen</strong>.</li>
                                    <li>Tap <strong>Add</strong>. Linguistic Quest will launch like a native app.</li>
                                </ol>
                            ) : (
                                <ol className="mt-4 space-y-3 text-sm text-lq-ink-700 list-decimal list-inside">
                                    <li>Open the browser menu (︙ or ⋯) in the top right.</li>
                                    <li>Tap <strong>Install app</strong> or <strong>Add to Home Screen</strong>.</li>
                                    <li>Confirm. The app will appear on your home screen or Start menu.</li>
                                </ol>
                            )}
                            <p className="mt-4 text-xs text-lq-ink-500">Installing puts Linguistic Quest one tap away, with its own icon, and launches full-screen without the browser bar.</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
